import _assign from "lodash/assign";
import _isEmpty from "lodash/isEmpty";
import _reduce from "lodash/reduce";

import { Controller } from "stimulus";

import EditorJS from "@editorjs/editorjs";

// PLUGINS

// inline
import BoldInline from "./plugins_editor/bold_inline/bold_inline";
import ItalicInline from "./plugins_editor/italic_inline/italic_inline";
import LinkInline from "./plugins_editor/link_inline/link_inline";
import SubInline from "./plugins_editor/sub_inline/sub_inline";
import SupInline from "./plugins_editor/sup_inline/sup_inline";
// blocks
import TitleBlock from "./plugins_editor/title_block/title_block";
import TextBlock from "./plugins_editor/text_block/text_block";
import TableBlock from "./plugins_editor/table_block/table_block";
import FrameBlock from "./plugins_editor/frame_block/frame_block";
import ListBlock from "./plugins_editor/list_block/list_block";
import LinkBlock from "./plugins_editor/link_block/link_block";
import AccordionBlock from "./plugins_editor/accordion_block/accordion_block";
import ButtonBlock from "./plugins_editor/button_block/button_block";
import VerbatimBlock from "./plugins_editor/verbatim_block/verbatim_block";
import VideoBlock from "./plugins_editor/video_block/video_block";

// CLASS
export default class extends Controller {
  static targets = ["holder", "blocks"];

  initialize() {
    this.editorError = { message: "", data: [] };
  }

  connect() {
    this.initEditor();
  }

  initEditor(data) {
    let editorData = data || {};
    this.plugins = this.element.dataset.availablePlugins.split(" | ");

    if (_isEmpty(editorData) && this.hasBlocksTarget) {
      try {
        editorData = JSON.parse(this.blocksTarget.value);
      } catch (err) {
        console.error("Editor data is invalid JSON");
      }
    }

    // Utiliser le placeholder personnalisé s'il est défini dans les attributs de données
    const placeholder = this.element.dataset.placeholder || "Votre texte...";

    this.editor = new EditorJS({
      readOnly: Boolean(this.data.get("read-only")),
      holder: this.holderTarget.id, // ex: https://github.com/codex-team/editor.js/pull/1539/commits/e6799d2b7fde46a839d2b1f2ff725a7efe64dc55
      placeholder: placeholder,
      defaultBlock: this.element.dataset.defaultPlugin,
      inlineToolbar: ["bold-inline", "italic-inline", "link-inline", "sub-inline", "sup-inline"],
      tools: this.toolsConfig,
      i18n: this.i18nConfig,
      data: editorData,
      minHeight: 20,
      logLevel: "ERROR",
    });

    this.typingTimer;
    this.doneTypingInterval = 222;
  }

  disconnect() {
    // TODO: checker l'utilité
    if (this.editor instanceof EditorJS) {
      this.editor.destroy();
    }
  }

  reloadEditor(editorData) {
    this.editor.destroy();
    this.initEditor(editorData);
  }

  onKeypress() {
    clearTimeout(this.typingTimer);
    this.typingTimer = setTimeout(() => this.save(), this.doneTypingInterval); // WAIT END OF TYPING (222ms)
  }

  save_after_plugin_action() {
    setTimeout(() => this.save(), this.doneTypingInterval);
  }

  save() {
    clearTimeout(this.typingTimer);
    return this.editor.save().then((outputData) => {
      const errors = _reduce(
        outputData.blocks,
        (result, block, index) => {
          if (block.data._errors) {
            const e = block.data._errors;
            e.index = index;
            result.push(e);
          }

          return result;
        },
        []
      );

      if (errors.length > 0) {
        throw Object.create(this.editorError, {
          message: { value: "editor errors" },
          data: { value: errors },
        });
      }

      if (this.hasBlocksTarget) {
        // update the hidden input field value
        this.blocksTarget.value = JSON.stringify(outputData);
      }

      return outputData;
    });
  }

  // GETTERS & SETTERS

  get toolsConfig() {
    const toolsConfig = {
      // INLINE
      "bold-inline": {
        class: BoldInline,
        shortcut: "CMD+B",
      },
      "italic-inline": {
        class: ItalicInline,
        shortcut: "CMD+I",
      },
      "link-inline": {
        class: LinkInline,
        shortcut: "CMD+K",
        config: {
          placeholder: "https://...",
        },
      },
      "sub-inline": SubInline,
      "sup-inline": SupInline,
      // BLOCK
      text: {
        class: TextBlock,
        inlineToolbar: this.element.dataset.inlineAvailableTools
          ? this.element.dataset.inlineAvailableTools.split(",")
          : true,
      },
    };

    if (this.element.dataset.availablePlugins.includes("title")) {
      _assign(toolsConfig, {
        title: {
          class: TitleBlock,
          inlineToolbar: ["italic-inline", "sub-inline", "sup-inline"],
          config: {
            levels: [3, 4],
          },
        },
      });
    }

    if (this.element.dataset.availablePlugins.includes("frame")) {
      _assign(toolsConfig, {
        list: {
          class: ListBlock,
          inlineToolbar: true,
        },
        frame: {
          class: FrameBlock,
          inlineToolbar: true,
        },
      });
    }

    if (this.element.dataset.availablePlugins.includes("link")) {
      _assign(toolsConfig, {
        link: {
          class: LinkBlock,
          config: {
            disableSwitch: false,
          },
        },
      });
    }

    if (this.element.dataset.availablePlugins.includes("verbatim")) {
      _assign(toolsConfig, {
        verbatim: VerbatimBlock,
        button: {
          class: ButtonBlock,
          config: {
            textPlaceholder: "texte du bouton...",
            hrefPlaceholder: "url du lien associé...",
            defaultModifiers: ["is-red", "is-centered"],
          },
        },
        accordion: {
          class: AccordionBlock,
          inlineToolbar: true,
        },
      });
    }

    if (this.element.dataset.availablePlugins.includes("table")) {
      _assign(toolsConfig, {
        table: {
          class: TableBlock,
          inlineToolbar: true,
          config: {
            placeholder: "à remplir",
          },
        },
      });
    }

    if (this.element.dataset.availablePlugins.includes("video")) {
      _assign(toolsConfig, {
        video: {
          class: VideoBlock,
          config: {
            placeholder: "urls de vidéo reconnues : youtube, dailymotion, viméo et ina",
          },
        },
      });
    }

    return toolsConfig;
  }

  get i18nConfig() {
    return {
      messages: {
        ui: {
          inlineToolbar: {
            converter: {
              "Convert to": "convertir en",
            },
          },
        },
        toolNames: {
          "bold-inline": "gras",
          "italic-inline": "italique",
          "link-inline": "lien",
          "sub-inline": "indice",
          "sup-inline": "exposant",
          frame: "encadré",
          list: "liste",
          table: "tableau",
          link: "lien",
        },
        tools: {
          button: {
            title: "bouton",
          },
          frame: {
            title: "encadré",
          },
          list: {
            title: "liste",
          },
          table: {
            title: "tableau",
          },
          title: {
            title: "titre",
          },
          link: {
            title: "lien",
          },
          video: {
            title: "video",
          },
        },
      },
    };
  }
}
